// ui-molecule-paginator.component.ts
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { I18nMatPaginatorIntl } from './providers/mat-paginator-intl-i18n';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'ui-molecule-paginator',
  standalone: true,
  imports: [
    MatPaginator,
    UiAtomButtonComponent,
    UiMoleculeInputComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
  templateUrl: './ui-molecule-paginator.component.html',
  styleUrl: './ui-molecule-paginator.component.scss',
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: I18nMatPaginatorIntl,
    },
  ],
})
export class UiMoleculePaginatorComponent implements AfterViewInit, OnChanges {
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50];
  @Input() showFirstLastButtons: boolean = true;

  @Input() showClearFiltersButton: boolean = false;
  @Input() clearFiltersDisabled: boolean = false;
  @Input() showExportButton: boolean = true;
  @Input() exportLabelKey: string = 'tables.actions.export';
  @Input() exportTooltipKey: string = 'tables.actions.exportExcel';
  @Input() showGlobalSearch: boolean = true;

  // Valores del paginador configurables desde el padre
  @Input() length: number = 0;
  @Input() pageIndex: number = 0;
  @Input() pageSize: number = 10;

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() clearFiltersClick = new EventEmitter<void>();
  @Output() exportClick = new EventEmitter<void>();
  @Output() globalSearch = new EventEmitter<string>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** FormControl para el buscador global */
  protected searchControl = new FormControl('');
  protected isSearching = false;
  private searchTimeout: any;

  constructor() {
    // Escuchar cambios en el buscador
    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged(), // Solo emitir si el valor cambió
      )
      .subscribe((value) => {
        // Limpiar timeout anterior
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        // Mostrar loader inmediatamente si hay texto válido
        if (value && value.trim().length >= 3) {
          this.isSearching = true;

          // Esperar 500ms y luego emitir + ocultar loader
          this.searchTimeout = setTimeout(() => {
            this.globalSearch.emit(value || '');
            this.isSearching = false;
          }, 500);
        } else if (!value || value.trim() === '') {
          // Solo emitir cuando se borra completamente (para resetear filtro)
          this.isSearching = false;
          this.globalSearch.emit('');
        } else {
          // 1-2 caracteres: no emitir nada, solo limpiar estado
          this.isSearching = false;
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.length = this.length ?? 0;
      this.paginator.pageIndex = this.pageIndex ?? 0;
      this.paginator.pageSize = this.pageSize ?? 10;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.paginator) return;

    if (changes['length']) {
      this.paginator.length = this.length ?? 0;
    }
    if (changes['pageIndex']) {
      this.paginator.pageIndex = this.pageIndex ?? 0;
    }
    if (changes['pageSize']) {
      this.paginator.pageSize = this.pageSize ?? 10;
    }
  }

  getPaginator(): MatPaginator {
    return this.paginator;
  }

  onClearFiltersClick(): void {
    this.clearFiltersClick.emit();
  }

  onExportClick(): void {
    this.exportClick.emit();
  }

  clearSearch(): void {
    this.isSearching = false;
    this.searchControl.setValue('');
    this.globalSearch.emit('');
  }

  /** Método para indicar que la búsqueda terminó (llamado desde el componente padre) */
  public searchCompleted(): void {
    this.isSearching = false;
  }
}
